import type { StoreApi } from "zustand";

import type { AuthState, LogoutReason } from "@nasam/kernel";
import type { AuthRepository, LoginRequest, LoginResult } from "../domain";

export interface AuthController {
  initialize(): Promise<void>;
  login(request: LoginRequest): Promise<LoginResult>;
  /**
   * `beforeSession` runs once the code is accepted, before the session is
   * stored -- storing it sends the guest-only OTP route away, so a screen
   * that shows a "verified" moment first awaits it here.
   */
  verifyOtp(code: string, beforeSession?: () => Promise<void>): Promise<void>;
  resendOtp(): Promise<void>;
  logout(reason?: LogoutReason): Promise<void>;
}

export function createAuthController(
  repository: AuthRepository,
  store: StoreApi<AuthState>,
): AuthController {
  return {
    async initialize() {
      const existing = store.getState();
      if (existing.accessToken !== null && existing.user !== null) {
        existing.setInitialized(true);
        return;
      }

      try {
        const session = await repository.refreshAccessToken();
        store.getState().setSession(session.accessToken, session.user);
      } catch {
        store.getState().clearSession(null);
      } finally {
        store.getState().setInitialized(true);
      }
    },
    async login(request) {
      const result = await repository.login(request);
      if (result.requiresOtp) {
        store.getState().beginOtp(request.username);
      } else {
        store.getState().setSession(result.accessToken, result.user);
      }
      return result;
    },
    async verifyOtp(code, beforeSession) {
      const username = store.getState().pendingOtpUsername;
      if (username === null) {
        throw new Error("No OTP login is currently in progress.");
      }
      const session = await repository.verifyOtp({ username, code });
      await beforeSession?.();
      store.getState().setSession(session.accessToken, session.user);
    },
    async resendOtp() {
      const username = store.getState().pendingOtpUsername;
      if (username === null) {
        throw new Error("No OTP login is currently in progress.");
      }
      await repository.resendOtp({ username });
    },
    async logout(reason = "manual") {
      try {
        await repository.logout();
      } finally {
        store.getState().clearSession(reason);
      }
    },
  };
}
